import { useCallback } from 'react'
import { GraphQLError, IAPIError } from 'types'
import { shouldTrackError, trackError } from 'services/analytics'
import { getErrorSeverity, getErrorText } from 'services/error'
import { useSnackbar } from './redux'

interface IErrorDisplayerOptions {
  /**
   * defaults to true. When true, errors are tracked on Sentry.
   */
  track?: boolean
  /**
   * defaults to false. When true, errors are always tracked on Sentry, even if the error is marked to be discarded.
   */
  forceTrack?: boolean
  trackingExtraData?: Record<string, any>
}

export function useErrorDisplayer(options: IErrorDisplayerOptions = {}) {
  const { showSnackbarMessage } = useSnackbar()

  const displayError = useCallback((error: IAPIError | Error | GraphQLError, optionsOverride: IErrorDisplayerOptions = {}) => {
    const combinedOptions = { ...options, ...optionsOverride }
    const track = combinedOptions?.track ?? true
    const forceTrack = combinedOptions?.forceTrack ?? false
    if (track && (shouldTrackError(error) || forceTrack)) trackError(error, getErrorSeverity(error), combinedOptions?.trackingExtraData)
    showSnackbarMessage(getErrorText(error) ?? 'Unknown error', { level: 'error' })
  }, [showSnackbarMessage])

  const displayMutationErrors = useCallback((errors: readonly GraphQLError[], optionsOverride: IErrorDisplayerOptions = {}) => {
    // TODO: handle multiple errors?
    if (errors?.length > 0) {
      const combinedOptions = { ...options, ...optionsOverride }
      const track = combinedOptions?.track ?? true
      const forceTrack = combinedOptions?.forceTrack ?? false
      if (track && (shouldTrackError(errors[0]) || forceTrack)) {
        trackError(errors[0], getErrorSeverity(errors[0]), combinedOptions?.trackingExtraData)
      }
      showSnackbarMessage(getErrorText(errors[0]) ?? 'Unknown error', { level: 'error' })
      return true
    }
    return false
  }, [showSnackbarMessage])

  return {
    displayError,
    displayMutationErrors,
  }
}
